import type { StaleDataEventPayload } from './socket.types';

type StaleDataSyncEvent = 'stale_data_update' | 'client_data_updated';

type StaleDataTabMessage = {
  id: string;
  sourceTabId: string;
  event: StaleDataSyncEvent;
  payload: StaleDataEventPayload;
};

type StaleDataTabMessageHandler = (
  event: StaleDataSyncEvent,
  payload: StaleDataEventPayload,
) => void;

const CHANNEL_NAME = 'peanuds:stale-data';
const MAX_SEEN_MESSAGES = 200;

const createMessageId = (): string => {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
};

const sourceTabId = createMessageId();
const seenMessageIds = new Set<string>();
const seenMessageQueue: string[] = [];

let channel: BroadcastChannel | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }

  if (channel === null) {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }

  return channel;
};

const getPayloadCorrelationId = (payload: StaleDataEventPayload): string | null => {
  if ('correlationId' in payload && typeof payload.correlationId === 'string') {
    return payload.correlationId;
  }

  return null;
};

const getMessageId = (event: StaleDataSyncEvent, payload: StaleDataEventPayload): string => {
  const correlationId = getPayloadCorrelationId(payload);

  if (correlationId !== null) {
    return `${event}:${correlationId}`;
  }

  return createMessageId();
};

const isStaleDataTabMessage = (value: unknown): value is StaleDataTabMessage => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<StaleDataTabMessage>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.sourceTabId === 'string' &&
    (candidate.event === 'stale_data_update' || candidate.event === 'client_data_updated') &&
    typeof candidate.payload === 'object' &&
    candidate.payload !== null
  );
};

const markMessageSeen = (id: string): boolean => {
  if (seenMessageIds.has(id)) {
    return false;
  }

  seenMessageIds.add(id);
  seenMessageQueue.push(id);

  if (seenMessageQueue.length > MAX_SEEN_MESSAGES) {
    const removedId = seenMessageQueue.shift();

    if (removedId !== undefined) {
      seenMessageIds.delete(removedId);
    }
  }

  return true;
};

export const claimStaleDataTabMessage = (
  event: StaleDataSyncEvent,
  payload: StaleDataEventPayload,
): StaleDataTabMessage | null => {
  const message: StaleDataTabMessage = {
    id: getMessageId(event, payload),
    sourceTabId,
    event,
    payload,
  };

  if (!markMessageSeen(message.id)) {
    return null;
  }

  return message;
};

export const publishStaleDataTabMessage = (message: StaleDataTabMessage): void => {
  getChannel()?.postMessage(message);
};

export const subscribeToStaleDataTabMessages = (
  handler: StaleDataTabMessageHandler,
): (() => void) => {
  const syncChannel = getChannel();

  if (syncChannel === null) {
    return () => undefined;
  }

  const handleMessage = (messageEvent: MessageEvent<unknown>): void => {
    const message = messageEvent.data;

    if (!isStaleDataTabMessage(message) || message.sourceTabId === sourceTabId) {
      return;
    }

    if (!markMessageSeen(message.id)) {
      return;
    }

    handler(message.event, message.payload);
  };

  syncChannel.addEventListener('message', handleMessage);

  return () => {
    syncChannel.removeEventListener('message', handleMessage);
  };
};
